import React from 'react';
import { View, Text, TouchableOpacity, Image, Share, ViewStyle } from 'react-native';
import type { IMessage } from '@/types/admin/messages';
import { SVG_COMPONENTS as ADMIN_SVG_COMPONENTS } from '@/assets/admin';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { isMessageSentByUser } from '@/utils/residents/messages/messageUtils';
import { formatMessageTime } from '@/utils/common/formatting';
import { logger } from '@/utils/common/logger';

type Props = {
  message: IMessage;
  authUserId?: number;
  onOpenImageViewer: (urls: string[], startIndex: number) => void;
  onOpenDocument: (url: string, title?: string) => void;
};

const AttachmentMessageMobile: React.FC<Props> = ({ message, authUserId, onOpenImageViewer, onOpenDocument }) => {
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  if (!message.files || message.files.length === 0) return null;

  const imageFiles = message.files.filter((f) => (f.type || f.label)?.toLowerCase().startsWith('image/') || (f.fileUrl || '').match(/\.(png|jpe?g|gif|webp)$/i));
  const documentFiles = message.files.filter((f) => !imageFiles.includes(f));

  const isSender = isMessageSentByUser(message.sender?.id, authUserId, message.isAdmin);
  const bubbleAlign: Pick<ViewStyle, 'alignSelf'> = { alignSelf: isSender ? 'flex-end' : 'flex-start' };

  return (
    <View style={[styles.messageBubble, bubbleAlign, { backgroundColor: colors.card.bg, borderWidth: 1, borderColor: semantic.border.secondary }]}>
      {!!imageFiles.length && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {imageFiles.map((file, idx) => (
            <TouchableOpacity
              key={`${message.id}-img-${idx}`}
              onPress={() => {
                const urls = imageFiles.map((f) => f.fileUrl || (f as any).uri || '').filter(Boolean);
                onOpenImageViewer(urls, idx);
              }}
              onLongPress={async () => {
                try {
                  const url = file.fileUrl || (file as any).uri || '';
                  await Share.share({ url, message: url });
                } catch (e) {
                  logger.warn('attachmentShareFailed', e);
                }
              }}
              activeOpacity={0.8}
              style={{ marginRight: 6, marginBottom: 6 }}
            >
              <Image source={{ uri: file.fileUrl || (file as any).uri }} style={{ width: 140, height: 140, borderRadius: 8, backgroundColor: colors.bg.base }} />
            </TouchableOpacity>
          ))}
        </View>
      )}
      {documentFiles.map((file, idx) => (
        <TouchableOpacity
          key={`${message.id}-doc-${idx}`}
          onPress={() => {
            const url = file.fileUrl || (file as any).uri;
            if (!url) return;
            onOpenDocument(url, file.label || file.name || 'Document');
          }}
          style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}
        >
          <ADMIN_SVG_COMPONENTS.PAPERCLIP width={16} height={16} color={colors.text.secondary} />
          <Text style={{ marginLeft: 8, color: colors.text.primary }} numberOfLines={1}>{file.label || file.name}</Text>
        </TouchableOpacity>
      ))}
      <Text style={styles.timeStamp}>{formatMessageTime(message.createdAt)}</Text>
    </View>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => ({
  messageBubble: {
    maxWidth: '80%',
    borderRadius: 18,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginVertical: 4,
  },
  timeStamp: { fontSize: 11, color: colors.text.tertiary, marginTop: 4, textAlign: 'right' as const },
} as const);

export default AttachmentMessageMobile;


